/*
 * Doverfield Home: shared theme behaviour (loaded on every page as a module).
 *
 * Custom elements
 *   <sticky-header>         hides on scroll down, reveals on scroll up
 *   <menu-drawer>           mobile navigation drawer (extends ModalDrawer)
 *   <details-disclosure>    desktop mega menu around a <details> element
 *   <announcement-rotator>  rotates announcement bar messages
 *   <accordion-item>        animated <details> accordion (menu, footer, FAQ, filters)
 *   <quantity-input>        quantity stepper with clamping (max 99)
 *   <promo-countdown>       live countdown to the end of settings.promotion
 *
 * Exports (import from './global.js' in other modules, or use
 * window.DoverfieldTheme in classic scripts)
 *   trapFocus(container, elementToFocus?)   keep Tab inside container
 *   removeTrapFocus(elementToFocus?)        release the most recent trap
 *   onKeyUpEscape(event)                    close the nearest open <details>
 *   lockScroll() / unlockScroll()           counted page scroll lock
 *   afterTransition(element, callback)      run callback when a transition ends
 *   prefersReducedMotion()
 *   showToast(message)                      the one toast, for errors outside forms
 *   ModalDrawer                             base class for drawers (cart, filters)
 *
 * Document events
 *   cart:updated  (from cart-store.js)  updates [data-cart-count] badges
 *   search:open   dispatched (cancelable) when a [data-search-open] trigger is
 *                 clicked. The predictive search overlay should listen on
 *                 document and call event.preventDefault() to take over;
 *                 if nobody does, a link follows its href and a button
 *                 goes to its data-href.
 *   cart:open     dispatched (cancelable) when a [data-cart-open] link is
 *                 clicked and after a quick add succeeds. The cart drawer
 *                 should call event.preventDefault() and open itself
 *                 (detail.trigger is the element to return focus to).
 */

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
export const prefersReducedMotion = () => reducedMotionQuery.matches;

const desktopQuery = window.matchMedia('(min-width: 990px)');
const tabletQuery = window.matchMedia('(min-width: 750px)');

/* ------------------------------------------------------------------------ */
/* Strings rendered by theme.liquid (all copy goes through the locale file)  */
/* ------------------------------------------------------------------------ */

let themeStrings = null;
function strings() {
  if (themeStrings) return themeStrings;
  try {
    themeStrings = JSON.parse(document.getElementById('ThemeStrings')?.textContent || '{}');
  } catch {
    themeStrings = {};
  }
  return themeStrings;
}

/* ------------------------------------------------------------------------ */
/* Helpers                                                                   */
/* ------------------------------------------------------------------------ */

const FOCUSABLE = [
  'summary',
  'a[href]',
  'button:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex^="-"])',
].join(',');

export function getFocusableElements(container) {
  return Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => {
    if (el.closest('[inert]')) return false;
    if (el.tabIndex < 0) return false;
    return el.getClientRects().length > 0;
  });
}

const trapStack = [];

function onTrapKeydown(event) {
  const container = trapStack[trapStack.length - 1];
  if (!container || event.key !== 'Tab') return;
  const focusable = getFocusableElements(container);
  if (focusable.length === 0) {
    event.preventDefault();
    container.focus({ preventScroll: true });
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  const outside = !container.contains(active);
  if (event.shiftKey && (outside || active === first || active === container)) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (outside || active === last)) {
    event.preventDefault();
    first.focus();
  }
}

function onTrapFocusIn(event) {
  const container = trapStack[trapStack.length - 1];
  if (!container || container.contains(event.target)) return;
  const [first] = getFocusableElements(container);
  (first || container).focus({ preventScroll: true });
}

/** Keeps keyboard focus inside `container` until removeTrapFocus() is called. */
export function trapFocus(container, elementToFocus = container) {
  if (!container) return;
  const existing = trapStack.indexOf(container);
  if (existing !== -1) trapStack.splice(existing, 1);
  trapStack.push(container);
  if (trapStack.length === 1) {
    document.addEventListener('keydown', onTrapKeydown);
    document.addEventListener('focusin', onTrapFocusIn);
  }
  if (elementToFocus) {
    if (elementToFocus === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    elementToFocus.focus({ preventScroll: true });
  }
}

/** Releases the most recent focus trap (or `container` if given via the stack) and optionally moves focus. */
export function removeTrapFocus(elementToFocus = null, container = null) {
  if (container) {
    const index = trapStack.indexOf(container);
    if (index !== -1) trapStack.splice(index, 1);
  } else {
    trapStack.pop();
  }
  if (trapStack.length === 0) {
    document.removeEventListener('keydown', onTrapKeydown);
    document.removeEventListener('focusin', onTrapFocusIn);
  }
  if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus({ preventScroll: true });
}

/** Closes the nearest open <details> on Escape and returns focus to its summary. */
export function onKeyUpEscape(event) {
  if (event.key !== 'Escape') return;
  const openDetails = event.target.closest('details[open]');
  if (!openDetails) return;
  const summary = openDetails.querySelector(':scope > summary');
  openDetails.removeAttribute('open');
  summary?.setAttribute('aria-expanded', 'false');
  summary?.focus();
}

let scrollLocks = 0;
export function lockScroll() {
  scrollLocks += 1;
  document.documentElement.classList.add('is-scroll-locked');
}
export function unlockScroll() {
  scrollLocks = Math.max(0, scrollLocks - 1);
  if (scrollLocks === 0) document.documentElement.classList.remove('is-scroll-locked');
}

function toMs(value) {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return 0;
  return value.trim().endsWith('ms') ? n : n * 1000;
}

function longestTransition(element) {
  const style = getComputedStyle(element);
  const durations = style.transitionDuration.split(',');
  const delays = style.transitionDelay.split(',');
  let longest = 0;
  durations.forEach((duration, i) => {
    longest = Math.max(longest, toMs(duration) + toMs(delays[i % delays.length] || '0s'));
  });
  return longest;
}

/**
 * Calls `callback` once the element's current transition has finished, with
 * a timeout fallback (duration + 50ms) because transitionend does not fire
 * for zero-length or interrupted transitions. Returns a cancel function.
 */
export function afterTransition(element, callback) {
  const duration = element ? longestTransition(element) : 0;
  if (duration <= 0) {
    callback();
    return () => {};
  }
  let done = false;
  let timer = null;
  const finish = (event) => {
    if (event && event.target !== element) return;
    if (done) return;
    done = true;
    element.removeEventListener('transitionend', finish);
    clearTimeout(timer);
    callback();
  };
  element.addEventListener('transitionend', finish);
  timer = setTimeout(finish, duration + 50);
  return () => {
    done = true;
    element.removeEventListener('transitionend', finish);
    clearTimeout(timer);
  };
}

const forceReflow = (element) => element.getBoundingClientRect();

const defineElement = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

/* ------------------------------------------------------------------------ */
/* Toast                                                                     */
/* ------------------------------------------------------------------------ */

let toastRegion = null;
let toastTimer = null;

/** Shows the single toast used for errors that happen outside a form. */
export function showToast(message) {
  if (!message) return;
  if (!toastRegion) {
    toastRegion = document.createElement('div');
    toastRegion.className = 'toast-region';
    toastRegion.innerHTML = '<div class="toast" role="status" aria-live="polite"></div>';
    document.body.append(toastRegion);
  }
  const toast = toastRegion.firstElementChild;
  clearTimeout(toastTimer);
  toast.textContent = message;
  forceReflow(toast);
  toast.classList.add('is-visible');
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4000);
}

/* ------------------------------------------------------------------------ */
/* ModalDrawer: shared by the menu drawer, cart drawer and filter drawer     */
/* ------------------------------------------------------------------------ */

/**
 * Markup:
 *   <x-drawer class="drawer" id="..." hidden>
 *     <div class="drawer__scrim" data-drawer-close></div>
 *     <div class="drawer__panel drawer__panel--left|--right" role="dialog" aria-modal="true" aria-label="...">
 *       ... <button data-drawer-close data-drawer-initial-focus>...</button>
 *     </div>
 *   </x-drawer>
 * Any button with data-drawer-open and aria-controls="<drawer id>" opens it.
 */
export class ModalDrawer extends HTMLElement {
  connectedCallback() {
    if (this.drawerReady) return;
    this.drawerReady = true;
    this.panel = this.querySelector('.drawer__panel') || this;
    this.cancelClose = null;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-drawer-close]')) {
        event.preventDefault();
        this.close();
      }
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.stopPropagation();
        this.close();
      }
    });
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  open(trigger = document.activeElement) {
    if (this.isOpen) return;
    if (this.cancelClose) this.cancelClose();
    this.trigger = trigger instanceof HTMLElement ? trigger : null;
    this.hidden = false;
    this.classList.remove('is-closing');
    forceReflow(this.panel);
    this.classList.add('is-open');
    this.updateTriggers(true);
    lockScroll();
    const initial = this.querySelector('[data-drawer-initial-focus]') || this.panel;
    trapFocus(this.panel, initial);
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.classList.add('is-closing');
    this.updateTriggers(false);
    unlockScroll();
    removeTrapFocus(restoreFocus ? this.trigger : null, this.panel);
    this.cancelClose = afterTransition(this.panel, () => {
      this.classList.remove('is-closing');
      this.hidden = true;
      this.cancelClose = null;
    });
    this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
  }

  updateTriggers(expanded) {
    if (!this.id) return;
    document.querySelectorAll(`[aria-controls="${CSS.escape(this.id)}"]`).forEach((el) => {
      el.setAttribute('aria-expanded', String(expanded));
    });
  }
}

class MenuDrawer extends ModalDrawer {
  connectedCallback() {
    super.connectedCallback();
    desktopQuery.addEventListener('change', (event) => {
      if (event.matches) this.close({ restoreFocus: false });
    });
  }
}

/* ------------------------------------------------------------------------ */
/* Sticky header                                                             */
/* ------------------------------------------------------------------------ */

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.lastY = window.scrollY;
    this.ticking = false;
    this.measure();
    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', () => this.measure(), { passive: true });
    this.addEventListener('focusin', () => this.reveal());
    this.update();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
  }

  measure() {
    // The announcement bar scrolls away; the header sticks below it.
    const bar = document.querySelector('.announcement-bar');
    this.start = bar ? bar.offsetHeight : 0;
  }

  get canHide() {
    if (prefersReducedMotion()) return false;
    if (document.documentElement.classList.contains('is-scroll-locked')) return false;
    if (this.querySelector('details[open]')) return false;
    if (this.querySelector(':focus-visible')) return false;
    return true;
  }

  update() {
    const y = Math.max(0, window.scrollY);
    this.classList.toggle('is-scrolled', y > this.start);
    const delta = y - this.lastY;
    if (Math.abs(delta) < 4) return;
    if (delta > 0 && y > this.start + 80 && this.canHide) {
      this.classList.add('is-hidden');
    } else if (delta < 0) {
      this.reveal();
    }
    this.lastY = y;
  }

  reveal() {
    this.classList.remove('is-hidden');
  }
}

/* ------------------------------------------------------------------------ */
/* Mega menu disclosure                                                      */
/* ------------------------------------------------------------------------ */

class DetailsDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector(':scope > summary');
    this.panel = this.details?.querySelector('.mega__panel');
    if (!this.details || !this.summary || !this.panel) return;

    this.summary.setAttribute('aria-expanded', 'false');
    if (this.panel.id) this.summary.setAttribute('aria-controls', this.panel.id);
    if (this.details.open) this.details.classList.add('is-open');

    this.openTimer = null;
    this.closeTimer = null;
    this.cancelClose = null;
    this.onDocumentClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };

    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      clearTimeout(this.openTimer);
      const fromKeyboard = event.detail === 0;
      if (this.isOpen) this.close();
      else this.open({ focusFirst: fromKeyboard });
    });

    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse' || !desktopQuery.matches) return;
      clearTimeout(this.closeTimer);
      if (this.isOpen) return;
      this.openTimer = setTimeout(() => this.open(), 120);
    });

    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.openTimer);
      if (!this.isOpen) return;
      this.closeTimer = setTimeout(() => this.close(), prefersReducedMotion() ? 0 : 200);
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close({ instant: true });
        this.summary.focus();
      }
    });

    this.addEventListener('focusout', (event) => {
      if (this.isOpen && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
    });

    desktopQuery.addEventListener('change', () => this.close({ instant: true }));
  }

  get isOpen() {
    return Boolean(this.details?.open) && !this.details.classList.contains('is-closing');
  }

  open({ focusFirst = false } = {}) {
    document.querySelectorAll('details-disclosure').forEach((other) => {
      if (other !== this && typeof other.close === 'function') other.close({ instant: true });
    });
    if (this.isOpen) return;
    if (this.cancelClose) this.cancelClose();
    this.details.classList.remove('is-closing');
    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    forceReflow(this.panel);
    this.details.classList.add('is-open');
    document.addEventListener('click', this.onDocumentClick);
    if (focusFirst) {
      const [first] = getFocusableElements(this.panel);
      first?.focus();
    }
  }

  close({ instant = false } = {}) {
    if (!this.details?.open) return;
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    document.removeEventListener('click', this.onDocumentClick);
    this.summary.setAttribute('aria-expanded', 'false');
    this.details.classList.remove('is-open');
    const finish = () => {
      this.details.open = false;
      this.details.classList.remove('is-closing');
      this.cancelClose = null;
    };
    if (instant) {
      if (this.cancelClose) this.cancelClose();
      finish();
      return;
    }
    if (this.details.classList.contains('is-closing')) return;
    this.details.classList.add('is-closing');
    this.cancelClose = afterTransition(this.panel, finish);
  }
}

/* ------------------------------------------------------------------------ */
/* Accordion                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * <accordion-item [data-desktop-open]>
 *   <details [open]>
 *     <summary class="accordion__trigger">Label {% render 'icon', name: 'plus', class: 'accordion__icon accordion__icon--plus' %}</summary>
 *     <div class="accordion__panel"><div class="accordion__inner"><div class="accordion__content">...</div></div></div>
 *   </details>
 * </accordion-item>
 *
 * data-desktop-open keeps the item open from 750px (the footer columns, whose
 * summary is hidden there in favour of a static heading) and closes it on
 * smaller screens.
 *
 * Inside a wrapper marked [data-accordion-single], opening an item closes the
 * other open items in that wrapper, so only one answer shows at a time.
 */
class AccordionItem extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector(':scope > details');
    this.summary = this.details?.querySelector(':scope > summary');
    this.panel = this.details?.querySelector(':scope > .accordion__panel');
    if (!this.details || !this.summary || !this.panel) return;
    this.cancel = null;
    this.locked = false;

    this.summary.setAttribute('aria-expanded', String(this.details.open));
    this.summary.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.locked) return;
      if (this.isOpen) this.close();
      else this.open();
    });
    this.details.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && this.isOpen && !this.locked && !event.target.closest('.drawer__panel')) {
        this.close();
        this.summary.focus();
      }
    });

    if (this.hasAttribute('data-desktop-open')) {
      const apply = () => {
        // From 750px the summary is hidden by CSS and a static heading is
        // shown instead, so the item only needs to be held open.
        if (tabletQuery.matches) {
          this.locked = true;
          this.setOpenInstantly(true);
        } else {
          this.locked = false;
          this.setOpenInstantly(false);
        }
      };
      apply();
      tabletQuery.addEventListener('change', apply);
    }
  }

  get isOpen() {
    return this.details.open && !this.details.classList.contains('is-closing');
  }

  setOpenInstantly(open) {
    if (this.cancel) this.cancel();
    this.details.classList.remove('is-opening', 'is-closing');
    this.details.classList.add('is-instant');
    this.details.open = open;
    this.summary.setAttribute('aria-expanded', String(open));
    forceReflow(this.details);
    this.details.classList.remove('is-instant');
  }

  open() {
    if (this.cancel) this.cancel();
    this.closeSiblings();
    this.details.classList.remove('is-closing');
    this.details.classList.add('is-opening');
    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');
    forceReflow(this.panel);
    this.details.classList.remove('is-opening');
  }

  closeSiblings() {
    const group = this.closest('[data-accordion-single]');
    if (!group) return;
    group.querySelectorAll('accordion-item').forEach((item) => {
      if (item !== this && item.closest('[data-accordion-single]') === group && item.isOpen && !item.locked) item.close();
    });
  }

  close() {
    if (this.cancel) this.cancel();
    this.summary.setAttribute('aria-expanded', 'false');
    this.details.classList.add('is-closing');
    this.cancel = afterTransition(this.panel, () => {
      this.details.open = false;
      this.details.classList.remove('is-closing');
      this.cancel = null;
    });
  }
}

/* ------------------------------------------------------------------------ */
/* Announcement bar                                                          */
/* ------------------------------------------------------------------------ */

class AnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.announcement__item'));
    if (this.items.length < 2) return;
    this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
    this.interval = Number(this.dataset.interval) || 5000;
    this.pausedBy = new Set();
    const pause = (reason) => () => this.pausedBy.add(reason);
    const resume = (reason) => () => this.pausedBy.delete(reason);
    this.addEventListener('pointerenter', pause('hover'));
    this.addEventListener('pointerleave', resume('hover'));
    this.addEventListener('focusin', pause('focus'));
    this.addEventListener('focusout', resume('focus'));
    reducedMotionQuery.addEventListener('change', () => this.start());
    this.start();
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  start() {
    clearInterval(this.timer);
    if (prefersReducedMotion()) {
      // Static: the stylesheet shows the messages inline instead.
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === 0));
      this.index = 0;
      return;
    }
    this.timer = setInterval(() => {
      if (this.pausedBy.size > 0 || document.hidden) return;
      this.next();
    }, this.interval);
  }

  next() {
    const current = this.items[this.index];
    this.index = (this.index + 1) % this.items.length;
    const incoming = this.items[this.index];
    current.classList.remove('is-active');
    current.classList.add('is-leaving');
    incoming.classList.remove('is-leaving');
    incoming.classList.add('is-active');
    afterTransition(current, () => current.classList.remove('is-leaving'));
  }
}

/* ------------------------------------------------------------------------ */
/* Quantity stepper                                                          */
/* ------------------------------------------------------------------------ */

class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.minus = this.querySelector('button[name="minus"]');
    this.plus = this.querySelector('button[name="plus"]');
    if (!this.input) return;
    this.current = this.clamp(this.input.value, 1);
    this.input.value = this.current;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[name="minus"], button[name="plus"]');
      if (!button || button.disabled) return;
      event.preventDefault();
      this.setValue(this.current + (button.name === 'plus' ? 1 : -1), true);
    });
    // Typed values are clamped in place before the change event bubbles on.
    this.input.addEventListener('change', () => this.setValue(this.input.value, false));
    this.input.addEventListener('focus', () => this.input.select());
    this.update();
  }

  get min() {
    const n = parseInt(this.input.min, 10);
    return Number.isFinite(n) ? Math.max(0, n) : 1;
  }

  get max() {
    const n = parseInt(this.input.max, 10);
    return Number.isFinite(n) ? Math.min(99, n) : 99;
  }

  clamp(value, fallback = this.current) {
    let n = Math.floor(Number(value));
    if (!Number.isFinite(n) || String(value).trim() === '') n = fallback;
    return Math.min(this.max, Math.max(this.min, n));
  }

  setValue(value, emit) {
    const next = this.clamp(value);
    this.input.value = next;
    if (next === this.current) {
      this.update();
      return;
    }
    this.current = next;
    this.update();
    if (emit) this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  update() {
    if (this.minus) {
      this.minus.disabled = this.current <= this.min;
      const removes = this.min === 0 && this.current === 1;
      const label = removes ? this.minus.dataset.labelRemove : this.minus.dataset.labelDecrease;
      if (label) this.minus.setAttribute('aria-label', label);
    }
    if (this.plus) this.plus.disabled = this.current >= this.max;
  }
}

/* ------------------------------------------------------------------------ */
/* Header cart count                                                         */
/* ------------------------------------------------------------------------ */

function cartLabel(element, count) {
  const template = count === 0 ? element.dataset.labelZero
    : count === 1 ? element.dataset.labelOne
      : element.dataset.labelOther;
  return (template || '').replace('[count]', String(count));
}

let lastCount = null;
function onCartUpdated(event) {
  const cart = event.detail && event.detail.cart;
  if (!cart) return;
  const count = Math.max(0, Number(cart.item_count) || 0);
  const changed = lastCount !== null && count !== lastCount;

  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.hidden = count === 0;
    if (changed && count > 0 && !prefersReducedMotion()) {
      badge.classList.add('is-bumped');
      setTimeout(() => badge.classList.remove('is-bumped'), 100);
    }
  });
  document.querySelectorAll('[data-cart-link]').forEach((link) => {
    const label = cartLabel(link, count);
    if (label) link.setAttribute('aria-label', label);
  });
  if (changed) {
    const live = document.querySelector('[data-cart-live]');
    const link = document.querySelector('[data-cart-link]');
    if (live && link) live.textContent = cartLabel(link, count);
  }
  lastCount = count;
}

/* ------------------------------------------------------------------------ */
/* Quick add on product cards                                                */
/* ------------------------------------------------------------------------ */

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function cartErrorMessage(error) {
  const s = strings();
  if (error && error.status === 422) return s.soldOut || error.description;
  if (error && error.status === 404) return s.notFound || error.description;
  return s.cartError || (error && error.description) || '';
}

function showAddedState(button) {
  const label = button.querySelector('[data-quick-add-label]');
  if (!label) return;
  const s = strings();
  clearTimeout(button.addedTimer);
  label.textContent = s.added || label.textContent;
  button.classList.add('is-added');
  button.addedTimer = setTimeout(() => {
    label.textContent = s.addToCart || label.textContent;
    button.classList.remove('is-added');
  }, 2000);
}

async function onQuickAdd(event) {
  const button = event.target.closest('[data-quick-add]');
  if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
  event.preventDefault();
  const id = Number(button.dataset.variantId);
  const cart = window.DoverfieldCart;
  if (!cart || !Number.isFinite(id)) {
    showToast(cartErrorMessage(null));
    return;
  }
  button.setAttribute('aria-busy', 'true');
  const started = performance.now();
  try {
    await cart.add({ id, quantity: 1 });
    // Keep the spinner visible long enough to read as feedback (at most 400ms in total).
    const elapsed = performance.now() - started;
    if (elapsed < 240 && !prefersReducedMotion()) await wait(240 - elapsed);
    button.removeAttribute('aria-busy');
    const handled = !document.dispatchEvent(new CustomEvent('cart:open', {
      cancelable: true,
      detail: { trigger: button, source: 'quick-add' },
    }));
    if (!handled) showAddedState(button);
  } catch (error) {
    button.removeAttribute('aria-busy');
    showToast(cartErrorMessage(error));
  }
}

/* ------------------------------------------------------------------------ */
/* Search and cart links: dispatch an event, fall back to the link           */
/* ------------------------------------------------------------------------ */

function interceptLink(selector, eventName) {
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(selector);
    if (!trigger || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const notHandled = document.dispatchEvent(new CustomEvent(eventName, {
      cancelable: true,
      detail: { trigger },
    }));
    if (!notHandled) {
      event.preventDefault();
    } else if (trigger.dataset.href && !trigger.hasAttribute('href')) {
      // A button trigger (e.g. the header search) has no link to follow, so
      // go to its data-href page when no overlay took over.
      window.location.href = trigger.dataset.href;
    }
  });
}

/* ------------------------------------------------------------------------ */
/* Newsletter signup (footer and home page)                                  */
/* ------------------------------------------------------------------------ */

/*
 * The forms are Shopify customer forms ({% form 'customer' %}, tagged
 * newsletter). This only checks the address before it is sent; Shopify
 * shows the success line or its own error after the page reloads.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function initNewsletter(form) {
  const input = form.querySelector('input[type="email"]');
  const error = form.querySelector('[data-newsletter-error]');
  const errorText = error?.querySelector('[data-newsletter-error-text]');
  const success = form.querySelector('[data-newsletter-success]');
  if (!input || !error || !success) return;
  const helpId = input.getAttribute('aria-describedby') || '';

  const validate = () => {
    const value = input.value.trim();
    let message = '';
    if (!value) message = form.dataset.errorRequired;
    else if (!EMAIL_PATTERN.test(value)) message = form.dataset.errorInvalid;
    if (message) {
      errorText.textContent = message;
      error.hidden = false;
      input.setAttribute('aria-invalid', 'true');
      input.setAttribute('aria-describedby', `${error.id} ${helpId}`.trim());
    } else {
      error.hidden = true;
      input.removeAttribute('aria-invalid');
      if (helpId) input.setAttribute('aria-describedby', helpId);
      else input.removeAttribute('aria-describedby');
    }
    return !message;
  };

  input.addEventListener('blur', () => {
    if (input.value.trim()) validate();
  });
  input.addEventListener('input', () => {
    success.hidden = true;
    if (input.getAttribute('aria-invalid') === 'true') validate();
  });
  form.addEventListener('submit', (event) => {
    if (!validate()) {
      event.preventDefault();
      input.focus();
    }
  });
}

/* ------------------------------------------------------------------------ */
/* Promotion countdown                                                       */
/* ------------------------------------------------------------------------ */

/*
 * <promo-countdown data-ends="ISO" data-starts="ISO" data-format="short|units">
 *
 * Counts down to the real end of the promotion (theme setting promotion_ends_at).
 * It never resets. The precision grows as the end gets closer:
 *   more than 7 days left   "46 days left"            (phase "calm")
 *   7 days to 48 hours      "5d 07h 12m" / d-h-m units (phase "week")
 *   last 48 hours           "31:12:40" / h-m-s units   (phase "final")
 *   after the end           "Deal ended"               (phase "ended")
 * "units" renders boxed digits that roll when they change; "short" is one
 * line of text. With data-seconds the seconds tick for the whole promotion:
 * "units" shows days, hours, minutes and seconds (hours, minutes and seconds
 * on the last day) and "short" reads "3d 16h 23m 41s left". The phase is
 * also set on the closest [data-promo] so the surrounding block can change
 * with it, and that block's
 * [data-promo-progress] gets --promo-left (share of the sale still to run).
 *
 * The countdown is visual only (aria-hidden in the markup); the static
 * ends label next to it is what screen readers get, so nothing is announced
 * every second.
 *
 * Review aid: ?promo_now=2026-11-30T20:00:00-05:00 pretends the clock is
 * at that time, so the final-week and last-day looks can be reviewed.
 */
const promoClockOffset = (() => {
  try {
    const value = new URLSearchParams(window.location.search).get('promo_now');
    const at = value ? Date.parse(value) : NaN;
    return Number.isFinite(at) ? at - Date.now() : 0;
  } catch {
    return 0;
  }
})();
const promoNow = () => Date.now() + promoClockOffset;
const pad2 = (n) => String(n).padStart(2, '0');
const promoCountdowns = new Set();
let promoTimer = null;

function tickPromoCountdowns() {
  promoCountdowns.forEach((el) => el.render());
  if (promoCountdowns.size === 0) {
    clearInterval(promoTimer);
    promoTimer = null;
  }
}

class PromoCountdown extends HTMLElement {
  connectedCallback() {
    this.ends = Date.parse(this.dataset.ends || '');
    this.starts = Date.parse(this.dataset.starts || '');
    if (!Number.isFinite(this.ends)) return;
    this.block = this.closest('[data-promo]');
    this.progress = this.block?.querySelector('[data-promo-progress]');
    this.render();
    promoCountdowns.add(this);
    if (!promoTimer) promoTimer = setInterval(tickPromoCountdowns, 1000);
  }

  disconnectedCallback() {
    promoCountdowns.delete(this);
  }

  render() {
    const s = strings().countdown || {};
    const left = Math.max(0, this.ends - promoNow());
    const total = Math.floor(left / 1000);
    const days = Math.floor(total / 86400);
    const hours = Math.floor(total / 3600) % 24;
    const minutes = Math.floor(total / 60) % 60;
    const seconds = total % 60;

    let phase = 'calm';
    if (left <= 0) phase = 'ended';
    else if (left <= 48 * 3600 * 1000) phase = 'final';
    else if (left <= 7 * 86400 * 1000) phase = 'week';
    if (this.hasAttribute('data-final-only')) this.hidden = phase !== 'final';
    if (this.dataset.phase !== phase) {
      this.dataset.phase = phase;
      if (this.block) this.block.dataset.promoPhase = phase;
      if (phase === 'ended') this.dispatchEvent(new CustomEvent('promo:ended', { bubbles: true }));
    }

    if (this.progress && Number.isFinite(this.starts) && this.ends > this.starts) {
      const share = Math.min(1, Math.max(0, left / (this.ends - this.starts)));
      this.progress.style.setProperty('--promo-left', share.toFixed(4));
    }

    const withSeconds = this.hasAttribute('data-seconds');
    if (phase === 'ended') {
      this.setText(s.ended || '');
    } else if (this.dataset.format === 'units' && withSeconds) {
      const units = days > 0
        ? [[days, s.days], [hours, s.hours], [minutes, s.minutes], [seconds, s.seconds]]
        : [[hours, s.hours], [minutes, s.minutes], [seconds, s.seconds]];
      this.setUnits(units);
    } else if (withSeconds && phase !== 'final' && s.shortSeconds) {
      this.setText(s.shortSeconds.replace('[days]', days).replace('[hours]', pad2(hours)).replace('[minutes]', pad2(minutes)).replace('[seconds]', pad2(seconds)));
    } else if (this.dataset.format === 'units' && phase !== 'calm') {
      const units = phase === 'final'
        ? [[days * 24 + hours, s.hours], [minutes, s.minutes], [seconds, s.seconds]]
        : [[days, s.days], [hours, s.hours], [minutes, s.minutes]];
      this.setUnits(units);
    } else if (phase === 'final') {
      this.setText((s.clock || '[time]').replace('[time]', `${pad2(days * 24 + hours)}:${pad2(minutes)}:${pad2(seconds)}`));
    } else if (phase === 'week') {
      this.setText((s.short || '').replace('[days]', days).replace('[hours]', pad2(hours)).replace('[minutes]', pad2(minutes)));
    } else {
      // Calm phase is always more than 7 days out, so the count is never 1.
      this.setText(fillCount(s.daysLeft, days));
    }
  }

  setText(text) {
    if (this.mode !== 'text') {
      this.mode = 'text';
      this.replaceChildren();
      this.classList.remove('promo-countdown--units');
    }
    if (this.textContent !== text) this.textContent = text;
  }

  // Two-digit boxes per unit. A digit that changes rolls up (no roll with
  // reduced motion, where the new digit simply replaces the old).
  setUnits(units) {
    const key = units.map(([, label]) => label).join('|');
    if (this.mode !== key) {
      this.mode = key;
      this.classList.add('promo-countdown--units');
      this.replaceChildren(...units.map(([, label]) => {
        const unit = document.createElement('span');
        unit.className = 'promo-countdown__unit';
        const digits = document.createElement('span');
        digits.className = 'promo-countdown__digits';
        for (let i = 0; i < 2; i += 1) {
          const digit = document.createElement('span');
          digit.className = 'promo-countdown__digit';
          digits.append(digit);
        }
        const name = document.createElement('span');
        name.className = 'promo-countdown__label';
        name.textContent = label || '';
        unit.append(digits, name);
        return unit;
      }));
    }
    const unitEls = this.querySelectorAll('.promo-countdown__unit');
    units.forEach(([value], i) => {
      const text = pad2(value);
      unitEls[i]?.querySelectorAll('.promo-countdown__digit').forEach((digit, k) => setRollingDigit(digit, text[k]));
    });
  }
}

function fillCount(template, count) {
  return (template || '').replace('[count]', String(count));
}

function setRollingDigit(el, value) {
  if (el.dataset.value === value) return;
  const first = el.dataset.value === undefined;
  el.dataset.value = value;
  const next = document.createElement('span');
  next.textContent = value;
  const old = el.querySelector('span:not(.is-out)');
  if (first || !old || prefersReducedMotion()) {
    el.replaceChildren(next);
    return;
  }
  next.className = 'is-in';
  el.append(next);
  old.classList.add('is-out');
  setTimeout(() => old.remove(), 400);
  requestAnimationFrame(() => requestAnimationFrame(() => next.classList.remove('is-in')));
}

/*
 * Promotion badges on product cards press on once, when they first come into
 * view. The badge is visible without JavaScript; with it, .js hides badges
 * marked [data-promo-stamp] until they are about to scroll in.
 */
function initPromoStamps() {
  const stamps = document.querySelectorAll('[data-promo-stamp]:not(.is-stamped)');
  if (!stamps.length) return;
  if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
    stamps.forEach((el) => el.classList.add('is-stamped', 'is-stamp-static'));
    return;
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-stamped');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px' });
  // Badges already on screen stamp straight away rather than waiting for the
  // observer's first callback.
  stamps.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.bottom > 0 && rect.top < window.innerHeight) el.classList.add('is-stamped');
    else observer.observe(el);
  });
}

/* ------------------------------------------------------------------------ */
/* Boot                                                                      */
/* ------------------------------------------------------------------------ */

defineElement('promo-countdown', PromoCountdown);
defineElement('sticky-header', StickyHeader);
defineElement('menu-drawer', MenuDrawer);
defineElement('details-disclosure', DetailsDisclosure);
defineElement('accordion-item', AccordionItem);
defineElement('announcement-rotator', AnnouncementRotator);
defineElement('quantity-input', QuantityInput);

document.addEventListener('cart:updated', onCartUpdated);
document.addEventListener('click', onQuickAdd);
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.getAttribute('aria-controls'));
  if (drawer && typeof drawer.open === 'function') {
    event.preventDefault();
    drawer.open(opener);
  }
});
interceptLink('[data-search-open]', 'search:open');
interceptLink('[data-cart-open]', 'cart:open');

function boot() {
  document.querySelectorAll('[data-newsletter]').forEach(initNewsletter);
  initPromoStamps();
  // Pick up the cart count if cart-store.js finished before this listener existed.
  const cart = window.DoverfieldCart;
  if (cart && lastCount === null && typeof cart.get === 'function') {
    cart.get().then((data) => {
      if (lastCount === null) onCartUpdated({ detail: { cart: data } });
    }).catch(() => {});
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();

window.DoverfieldTheme = {
  trapFocus,
  removeTrapFocus,
  onKeyUpEscape,
  lockScroll,
  unlockScroll,
  afterTransition,
  prefersReducedMotion,
  getFocusableElements,
  showToast,
  ModalDrawer,
};
